import React, { useMemo } from "react";
import {
  ArrowDownLeft,
  ArrowUpRight,
  Repeat2,
  RefreshCw,
  Star,
  Wallet,
} from "lucide-react";
import { formatBalance } from "../../utils/dashboardHelpers";
import {
  buildCoinStats,
  buildDailyFlowSeries,
  COIN_RATE,
  compactMoney,
  summarizeFlowSeries,
} from "../../utils/walletAnalytics";

const BalanceCard = ({ kind, icon: Icon, label, value, hint, actions }) => {
  const display = formatBalance(value, { full: true });

  return (
    <article className={`dash-pulse-balance dash-pulse-balance-${kind}`}>
      <div className="dash-pulse-balance-body">
        <span className="dash-pulse-balance-icon">
          <Icon size={16} aria-hidden />
        </span>
        <em>{label}</em>
        <strong className="tabular-nums">{display}</strong>
        {hint ? <span className="dash-pulse-balance-hint">{hint}</span> : null}
      </div>
      <div className="dash-pulse-balance-actions">{actions}</div>
    </article>
  );
};

const DashboardPulse = ({
  cash = 0,
  coin = 0,
  analyticsTx = [],
  refreshing = false,
  onRefresh,
  onDeposit,
  onWithdraw,
  onBuyCoin,
  onSwapCoin,
}) => {
  const series = useMemo(() => buildDailyFlowSeries(analyticsTx, 14), [analyticsTx]);
  const flow = useMemo(() => summarizeFlowSeries(series.slice(-7)), [series]);
  const coinStats = useMemo(() => buildCoinStats(analyticsTx), [analyticsTx]);

  return (
    <section className="dash-pulse" aria-label="Wallet overview">
      <div className="dash-pulse-toolbar">
        <span className="dash-pulse-toolbar-label">Wallet</span>
        {onRefresh ? (
          <button
            type="button"
            className="dash-icon-btn"
            onClick={onRefresh}
            disabled={refreshing}
            aria-label="Refresh wallet balance"
            title="Refresh wallet"
          >
            <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          </button>
        ) : null}
      </div>
      <div className="dash-pulse-balances">
        <BalanceCard
          kind="cash"
          icon={Wallet}
          label="Cash"
          value={cash}
          hint={
            flow.count > 0
              ? `In ${compactMoney(flow.credits)} · Out ${compactMoney(flow.debits)} this week`
              : "No activity this week"
          }
          actions={
            <>
              <button type="button" onClick={onWithdraw} title="Withdraw Cash to mobile money">
                <ArrowDownLeft size={12} />
                Out
              </button>
              <button
                type="button"
                className="dash-pulse-action-primary"
                onClick={onDeposit}
                title="Deposit Cash via mobile money"
              >
                <ArrowUpRight size={12} />
                In
              </button>
            </>
          }
        />

        <BalanceCard
          kind="coin"
          icon={Star}
          label="Coin"
          value={coin}
          hint={
            coinStats.count > 0
              ? `Earned ${compactMoney(coinStats.earned)} · Spent ${compactMoney(coinStats.spent)}`
              : `${COIN_RATE} Coin = 1 Cash`
          }
          actions={
            <>
              <button type="button" onClick={onBuyCoin} title={`Buy Coin with Cash (1 Cash = ${COIN_RATE} Coin)`}>
                <ArrowUpRight size={12} />
                Buy
              </button>
              <button
                type="button"
                className="dash-pulse-action-primary"
                onClick={onSwapCoin}
                title={`Swap Coin to Cash (${COIN_RATE} Coin = 1 Cash)`}
              >
                <Repeat2 size={12} />
                Swap
              </button>
            </>
          }
        />
      </div>
    </section>
  );
};

export default DashboardPulse;
